/**
 * Project Settings -> "Automatic simulations": the rules that turn a
 * realtime detection into a fire simulation under current conditions and
 * publish it to the organisation's GIS, plus the project's recent runs.
 * One rule per detection kind. Organisation staff edit; everyone can read.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  AutomationRule,
  useCreateAutomationRule,
  useDeleteAutomationRule,
  useProjectAutomationRules,
  useUpdateAutomationRule,
} from '../../api/automationRules';
import { RealtimeKind } from '../../api/realtimeKinds';
import { REALTIME_SOURCE_TYPES, SOURCE_TYPE_LABELS, SourceType } from '../../types/dataSource';
import { formatWhen } from '../../utils/incidentRekindle';
import AutomationRunRow from '../incidents/AutomationRunRow';

const HAZARD_LEVELS: { value: number; label: string }[] = [
  { value: 1, label: 'Advice or higher' },
  { value: 2, label: 'Watch and Act or higher' },
  { value: 3, label: 'Emergency Warning only' },
];

const accent = '#00d4ad';
const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: accent },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accent },
};
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
    '&.Mui-focused fieldset': { borderColor: accent },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiSelect-icon': { color: 'rgba(255, 255, 255, 0.7)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.5)' },
};
const cellSx = { color: '#fff', borderBottomColor: 'rgba(255, 255, 255, 0.12)' };
const headSx = { ...cellSx, color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 };

// utils/api throws a plain {message, status, data} object, not an Error.
const errorMessage = (err: unknown): string =>
  (err as { message?: string } | null)?.message || 'Something went wrong';

function kindLabel(kind: string): string {
  return SOURCE_TYPE_LABELS[kind as SourceType] ?? kind;
}

export default function DetectionAutomationSection({
  projectId,
  onNotify,
}: {
  projectId: string | undefined;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}) {
  const { data, isLoading } = useProjectAutomationRules(projectId);
  const createRule = useCreateAutomationRule(projectId);
  const updateRule = useUpdateAutomationRule(projectId);
  const deleteRule = useDeleteAutomationRule(projectId);

  const [newKind, setNewKind] = useState<RealtimeKind | ''>('');
  const [newDuration, setNewDuration] = useState<string>('8');
  const [newPublish, setNewPublish] = useState(true);
  const [newLevel, setNewLevel] = useState(1);
  // Draft durations per rule, so typing does not PATCH on every keystroke.
  const [durationDrafts, setDurationDrafts] = useState<Record<number, string>>({});

  if (!projectId || isLoading || !data) return null;

  const canEdit = data.can_edit;
  const minHours = data.min_duration_hours;
  const maxHours = data.max_duration_hours;
  const usedKinds = new Set(data.rules.map((r) => r.kind));
  // Kinds that are live for this organisation and not yet ruled.
  const addableKinds = REALTIME_SOURCE_TYPES.filter(
    (kind) => data.kinds[kind] && !usedKinds.has(kind as RealtimeKind)
  );

  const parseHours = (value: string): number | null => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < minHours || n > maxHours) return null;
    return n;
  };

  const handleAdd = () => {
    const hours = parseHours(newDuration);
    if (!newKind) return;
    if (hours === null) {
      onNotify(`Duration must be a whole number between ${minHours} and ${maxHours} hours`, 'error');
      return;
    }
    createRule.mutate(
      {
        kind: newKind,
        duration_hours: hours,
        publish_to_gis: newPublish,
        min_level: newKind === 'hazard' ? newLevel : 1,
      },
      {
        onSuccess: () => {
          onNotify(`Automatic ${kindLabel(newKind).toLowerCase()} simulations switched on`, 'success');
          setNewKind('');
          setNewDuration('8');
          setNewPublish(true);
          setNewLevel(1);
        },
        onError: (err) => onNotify(errorMessage(err), 'error'),
      }
    );
  };

  const patch = (input: Parameters<typeof updateRule.mutate>[0]) =>
    updateRule.mutate(input, {
      onError: (err) => onNotify(errorMessage(err), 'error'),
    });

  const commitDuration = (rule: AutomationRule) => {
    const draft = durationDrafts[rule.id];
    if (draft === undefined) return;
    const hours = parseHours(draft);
    setDurationDrafts((d) => {
      const next = { ...d };
      delete next[rule.id];
      return next;
    });
    if (hours === null) {
      onNotify(`Duration must be a whole number between ${minHours} and ${maxHours} hours`, 'error');
      return;
    }
    if (hours !== rule.duration_hours) patch({ id: rule.id, duration_hours: hours });
  };

  const handleDelete = (rule: AutomationRule) =>
    deleteRule.mutate(rule.id, {
      onSuccess: () =>
        onNotify(`Automatic ${kindLabel(rule.kind).toLowerCase()} simulations switched off`, 'success'),
      onError: (err) => onNotify(errorMessage(err), 'error'),
    });

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: '#fff' }}>
        Automatic simulations
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
        When a detection of a chosen kind opens a new incident on this project, a fire is
        simulated from it straight away — from the detection point, the outline of a cluster of
        detections, or a mapped burnt-area perimeter — under the weather forecast in force at that
        moment, for the number of hours you set. With “Publish to GIS” on, the finished fire is
        added to your organisation’s GIS time layers (Admin → GIS Services) as soon as it
        completes. Rules run whether or not anyone receives alerts for this project. Every run
        is listed here and on the incident’s page.
      </Typography>

      {data.rules.some((r) => r.publish_to_gis) && !data.gis_configured && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2, color: '#fff' }}>
          GIS publishing is not available in this environment; simulations still run.
        </Alert>
      )}

      {data.rules.length > 0 && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>Detection</TableCell>
              <TableCell sx={headSx}>Simulate for</TableCell>
              <TableCell sx={headSx}>Publish to GIS</TableCell>
              <TableCell sx={headSx}>On</TableCell>
              <TableCell sx={headSx}>Last fired</TableCell>
              {canEdit && <TableCell sx={headSx} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.rules.map((rule) => (
              <TableRow key={rule.id}>
                <TableCell sx={cellSx}>
                  {kindLabel(rule.kind)}
                  {rule.kind === 'hazard' && (
                    <TextField
                      select
                      size="small"
                      value={rule.min_level}
                      disabled={!canEdit}
                      onChange={(e) => patch({ id: rule.id, min_level: Number(e.target.value) })}
                      sx={{ ...fieldSx, ml: 1, minWidth: 200 }}
                    >
                      {HAZARD_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    size="small"
                    type="number"
                    value={durationDrafts[rule.id] ?? String(rule.duration_hours)}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setDurationDrafts((d) => ({ ...d, [rule.id]: e.target.value }))
                    }
                    onBlur={() => commitDuration(rule)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                    inputProps={{ min: minHours, max: maxHours, step: 1 }}
                    InputProps={{ endAdornment: <span style={{ color: 'rgba(255,255,255,0.6)' }}>h</span> }}
                    sx={{ ...fieldSx, width: 110 }}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Switch
                    checked={rule.publish_to_gis}
                    disabled={!canEdit}
                    onChange={(e) => patch({ id: rule.id, publish_to_gis: e.target.checked })}
                    sx={switchSx}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  <Switch
                    checked={rule.enabled}
                    disabled={!canEdit}
                    onChange={(e) => patch({ id: rule.id, enabled: e.target.checked })}
                    sx={switchSx}
                  />
                </TableCell>
                <TableCell sx={cellSx}>
                  {rule.last_triggered_at
                    ? `${formatWhen(rule.last_triggered_at)} (${rule.trigger_count}×)`
                    : 'never'}
                </TableCell>
                {canEdit && (
                  <TableCell sx={cellSx} align="right">
                    <Tooltip title="Remove this rule">
                      <IconButton size="small" onClick={() => handleDelete(rule)} sx={{ color: '#fff' }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {canEdit && addableKinds.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            select
            size="small"
            label="Detection"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as RealtimeKind)}
            sx={{ ...fieldSx, minWidth: 180 }}
          >
            {addableKinds.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {kindLabel(kind)}
              </MenuItem>
            ))}
          </TextField>
          {newKind === 'hazard' && (
            <TextField
              select
              size="small"
              label="Alert level"
              value={newLevel}
              onChange={(e) => setNewLevel(Number(e.target.value))}
              sx={{ ...fieldSx, minWidth: 220 }}
            >
              {HAZARD_LEVELS.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  {level.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            size="small"
            type="number"
            label="Simulate for"
            value={newDuration}
            onChange={(e) => setNewDuration(e.target.value)}
            inputProps={{ min: minHours, max: maxHours, step: 1 }}
            InputProps={{ endAdornment: <span style={{ color: 'rgba(255,255,255,0.6)' }}>h</span> }}
            sx={{ ...fieldSx, width: 130 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newPublish}
                onChange={(e) => setNewPublish(e.target.checked)}
                sx={switchSx}
              />
            }
            label="Publish to GIS"
            sx={{ color: '#fff' }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            disabled={!newKind || createRule.isPending}
            onClick={handleAdd}
            sx={{ color: accent, borderColor: accent }}
          >
            Add rule
          </Button>
        </Box>
      )}
      {canEdit && data.rules.length === 0 && addableKinds.length === 0 && (
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
          No realtime detection kinds are switched on for your organisation yet, so there is
          nothing to automate.
        </Typography>
      )}
      {!canEdit && data.rules.length === 0 && (
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2 }}>
          No automatic simulations are set up for this project.
        </Typography>
      )}

      {data.runs.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            Recent runs
          </Typography>
          {data.runs.map((run) => (
            <AutomationRunRow key={run.id} run={run} showIncident light />
          ))}
        </Box>
      )}
    </Box>
  );
}
